import { PrimeTimesMatrix, PrimeTimeData, WeekdayIndex } from '../../hooks/types/primeTimesTypes';

/**
 * Converte o payload `primeTimes.hourlyByDay` do backend (168 células de 7 dias × 24 horas)
 * na matriz 7×12 (buckets de 2h) consumida pelo heatmap e pelos cards de Horários.
 *
 * O backend já entrega por célula: unique_plays, unique_views, conversions, revenue,
 * device_dominant e device_percentage. Aqui agregamos por bucket de 2h e derivamos:
 *  - deviceDominant/devicePercentage do bucket (a partir da contagem de plays por device)
 *  - conversionRate = conversions / playsUnique * 100 (não vem pronto do backend)
 *
 * Observação: conversions/revenue do backend hoje somam o histórico inteiro do vídeo
 * (não respeitam o filtro de data), enquanto plays/views respeitam — por isso a taxa
 * de conversão por célula pode ficar distorcida em janelas curtas.
 */
export function transformPrimeTimesToMatrix(primeTimes: any): PrimeTimesMatrix | null {
  if (!primeTimes || !primeTimes.hourlyByDay) {
    return null;
  }

  const matrix: PrimeTimeData[] = [];
  let maxPlays = 0;
  let maxViews = 0;

  // Agrupa as horas em buckets de 2h (0-2, 2-4, ...), 12 buckets por dia
  for (let weekday = 0; weekday < 7; weekday++) {
    for (let bucket = 0; bucket < 12; bucket++) {
      const startHour = bucket * 2;
      const endHour = startHour + 2;

      const cellsInBucket = primeTimes.hourlyByDay.filter(
        (cell: any) => cell.day === weekday && cell.hour >= startHour && cell.hour < endHour
      );

      let playsUnique = 0;
      let viewsUnique = 0;
      let conversions = 0;
      let revenue = 0;
      const deviceCounts: Record<string, number> = {};

      cellsInBucket.forEach((cell: any) => {
        playsUnique += cell.unique_plays || 0;
        viewsUnique += cell.unique_views || 0;
        conversions += cell.conversions || 0;
        revenue += cell.revenue || 0;

        // Agregar contagem de dispositivos (ponderada por plays únicos da hora)
        if (cell.device_dominant) {
          const device = cell.device_dominant;
          deviceCounts[device] = (deviceCounts[device] || 0) + (cell.unique_plays || 0);
        }
      });

      // Dispositivo dominante do bucket
      let deviceDominant = 'Desktop';
      let maxCount = 0;
      Object.entries(deviceCounts).forEach(([device, count]) => {
        if (count > maxCount) {
          deviceDominant = device;
          maxCount = count;
        }
      });

      const totalPlays = Object.values(deviceCounts).reduce((sum, count) => sum + count, 0);
      const devicePercentage = totalPlays > 0 ? Math.round((maxCount / totalPlays) * 100) : 0;

      // Taxa de conversão da célula (sobre plays únicos, espelha o conversion_rate global do summary).
      // Limitada a 100%: valores acima são artefato do backend (conversões somam o histórico inteiro,
      // enquanto plays respeitam o filtro de data) e não representam uma taxa real.
      const rawConversionRate = playsUnique > 0 ? (conversions / playsUnique) * 100 : 0;
      const conversionRate = Math.min(100, rawConversionRate);

      maxPlays = Math.max(maxPlays, playsUnique);
      maxViews = Math.max(maxViews, viewsUnique);

      matrix.push({
        weekday: weekday as WeekdayIndex,
        hourBucket: bucket,
        playsUnique,
        viewsUnique,
        deviceDominant,
        devicePercentage,
        conversions,
        revenue,
        conversionRate,
      });
    }
  }

  return {
    bucketHours: 2,
    timezone: 'America/Sao_Paulo',
    matrix,
    max: {
      playsUnique: maxPlays,
      viewsUnique: maxViews,
    },
  };
}
